import { makeAutoObservable } from 'mobx';
import { CurrentStartTime } from '../interfaces/timeTracking';
import {
  TrackingActivityInterface,
  TimeTrackingDataInterface,
  LogDataInterface,
  TimeEntryInputInterface,
  ListDropdownLInterface
} from '../../interfaces/TimeTrackingInterface';
import { TimeLogInterface } from '../../interfaces/TimeReportInterface';
import { ReportTimeFilterInterface } from '../../interfaces/ReportInterface';
export class TimeTrackingStore {
  currentStartTime: CurrentStartTime = { task_id: undefined, time: 0 };
  loading: boolean = false;
  activeTimeLogs: TrackingActivityInterface[] = [];
  myTimeLog: TimeLogInterface[] = [];
  myTimeLogFilter: ReportTimeFilterInterface | null = null;
  timeTackingData: TimeTrackingDataInterface[] = [];
  currentTimeTracking: TimeTrackingDataInterface = {
    active: true,
    date: '',
    day: '',
    formated_date: '',
    hours: 0.0,
    index: 0,
    logData: []
  };
  listDropdown: ListDropdownLInterface = {
    projectList: [],
    taskGroupList: [],
    taskList: []
  };
  timeEntryInput: TimeEntryInputInterface = {
    trackingStartTime: '',
    History: null,
    companyId: '',
    createdOn: undefined,
    date: '',
    formated_date: '',
    hours: '00:00',
    startTime: '00:00',
    id: undefined,
    isBillable: true,
    isInvoiced: false,
    notes: '',
    projectId: '',
    projectName: undefined,
    status: undefined,
    taskGroupId: undefined,
    taskGroupName: undefined,
    taskId: '',
    taskName: undefined,
    updated_at: undefined,
    day: undefined
  };

  constructor() {
    makeAutoObservable(this);
  }

  setCurrentStartTime = (startTime: CurrentStartTime) => {
    this.currentStartTime = startTime;
  };

  setActiveTimeLogs = (list: TrackingActivityInterface[]) => {
    this.activeTimeLogs = list || [];
  };

  setTimeTrackingLoader = (value: boolean) => {
    this.loading = !!value;
  };

  setMyTimeLog = (list: TimeLogInterface[]) => {
    this.myTimeLog = list || [];
  };

  setMyTimeLogFilter = (filter: ReportTimeFilterInterface) => {
    this.myTimeLogFilter = filter;
  };

  setTimeTackingData = (list: TimeTrackingDataInterface[]) => {
    this.timeTackingData = list || [];
  };

  setCurrentTimeTracking = (item: TimeTrackingDataInterface) => {
    this.currentTimeTracking = item;
  };

  clearCurrentTimeTracking = () => {
    this.currentTimeTracking = {
      active: true,
      date: '',
      day: '',
      formated_date: '',
      hours: 0.0,
      index: 0,
      logData: []
    };
  };

  updateTaskInStore = (updatedTask: LogDataInterface) => {
    // This ensures the total hours in the top slider update immediately
    // Using immutable map operation so MobX correctly observes the top-level assignment
    this.timeTackingData = this.timeTackingData.map((day: TimeTrackingDataInterface) => {
      const index = day.logData.findIndex((item) => item.id === updatedTask.id);
      if (index === -1) return day;
      return {
        ...day,
        logData: [...day.logData.slice(0, index), updatedTask, ...day.logData.slice(index + 1)]
      };
    });

    if (this.currentTimeTracking) {
      const index = this.currentTimeTracking.logData.findIndex((item) => item.id === updatedTask.id);
      if (index !== -1) {
        this.currentTimeTracking = {
          ...this.currentTimeTracking,
          logData: [
            ...this.currentTimeTracking.logData.slice(0, index),
            updatedTask,
            ...this.currentTimeTracking.logData.slice(index + 1)
          ]
        };
      }
    }
  };

  // listDropdown handlers (migrated from Redux)
  setListDropdown = (payload: Partial<ListDropdownLInterface>) => {
    // Mirror previous behavior: replace with payload
    this.listDropdown = payload as ListDropdownLInterface;
  };

  updateListDropdown = <K extends keyof ListDropdownLInterface>(args: {
    propsName: K;
    value: ListDropdownLInterface[K];
  }) => {
    const { propsName, value } = args;
    this.listDropdown = { ...this.listDropdown, [propsName]: value } as ListDropdownLInterface;
  };

  clearListDropdown = () => {
    this.listDropdown = { projectList: [], taskGroupList: [], taskList: [] };
  };

  // timeEntryInput handlers (migrated from Redux)
  setTimeEntryInput = (payload: Partial<TimeEntryInputInterface>) => {
    this.timeEntryInput = { ...this.timeEntryInput, ...(payload || {}) } as TimeEntryInputInterface;
  };

  updateTimeEntryInput = <K extends keyof TimeEntryInputInterface>(args: {
    propsName: K;
    value: TimeEntryInputInterface[K];
  }) => {
    const { propsName, value } = args;
    this.timeEntryInput = { ...this.timeEntryInput, [propsName]: value } as TimeEntryInputInterface;
  };

  clearTimeEntryInput = () => {
    this.timeEntryInput = {
      trackingStartTime: '',
      History: null,
      companyId: '',
      createdOn: undefined,
      date: '',
      formated_date: '',
      hours: '00:00',
      startTime: '00:00',
      id: undefined,
      isBillable: true,
      isInvoiced: false,
      notes: '',
      projectId: '',
      projectName: undefined,
      status: undefined,
      taskGroupId: undefined,
      taskGroupName: undefined,
      taskId: '',
      taskName: undefined,
      updated_at: undefined,
      day: undefined
    };
  };

  // Computed property — cached by MobX, only recomputes when activeTimeLogs[0].History changes
  get processedActiveHistory() {
    const history = this.activeTimeLogs[0]?.History;
    if (!history) {
      return { historyWithoutTypename: [], totalHours: 0 };
    }

    const historyWithoutTypename = history.map((item) => {
      const { __typename, ...other } = item;
      return other;
    });

    const totalHours = history.reduce((total, item) => total + (item.Hours || 0), 0);

    return { historyWithoutTypename, totalHours };
  }

  clearTimeTrackingData = () => {
    this.currentStartTime = { task_id: undefined, time: 0 };
    this.loading = false;
    this.activeTimeLogs = [];
    this.myTimeLog = [];
    this.myTimeLogFilter = null;
    this.timeTackingData = [];
    this.clearCurrentTimeTracking();
    this.clearListDropdown();
    this.clearTimeEntryInput();
  };
}
